import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditNoteIcon from '@mui/icons-material/EditNote';
import { ArcBaseContent } from '@src/core/components';
import { ArcButton, ArcCellInfo, ArcIconButton, ArcStack, ArcText, ArcTooltip } from '@src/core/ui';
import { ArcGoogleMap, setMapLocation, useConfirm } from '@src/core/controllers';
import { routesConfig } from '@src/core/routes';
import {
  useDeleteMissionTemplateMutation,
  useMaterializeMissionTemplateMutation,
  useUpdateMissionTemplateMutation,
} from '@src/features/order/redux';
import {
  selectSelectedMissionTemplate,
  setOrderFormData,
  setSelectedMissionTemplate,
} from '@src/features/order/pages/orders-page/redux';
import SaveTemplateDialog from '@src/features/order/pages/confirm-order-page/components/save-template-dialog';
import { ISaveTemplateForm } from '@src/features/order/pages/confirm-order-page/components/save-template-dialog/types';
import { OrderEntity } from '@src/features/order/entities';
import { trackError, toTrackedError } from '@src/core/sentry';
import {
  arcFormatCoordinateDisplay,
  arcToMapCoordinate,
  arcSpatium,
  arcVelocitas,
} from '@src/core/extensions';
import { EMPTY_STRING } from '@src/core/config';
import { formatDeliveryMode } from '@src/features/order/utils';
import MissionTemplatesMap from './map';

const labelProps = { fontSize: '14px' };
const textProps = { fontWeight: 400, fontSize: '16px' };
const headerButtonSx = {
  textTransform: 'none',
  padding: '4px 8px',
  fontSize: '12px',
  marginLeft: '12px',
  '& .MuiTypography-root': { textTransform: 'none' },
} as const;

export const MissionTemplatesContent: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const template = useSelector(selectSelectedMissionTemplate);
  const { confirm } = useConfirm();
  const [editDialogOpen, setEditDialogOpen] = React.useState(false);
  const [materializeMissionTemplate, { isLoading: isProceeding }] = useMaterializeMissionTemplateMutation();
  const [updateMissionTemplate, { isLoading: isUpdating }] = useUpdateMissionTemplateMutation();
  const [deleteMissionTemplate, { isLoading: isDeleting }] = useDeleteMissionTemplateMutation();

  const onProceed = () => {
    if (!template) return;
    materializeMissionTemplate({
      id: template.id,
      body: {
        ...(template.packageContent ? { packageContent: template.packageContent } : {}),
        ...(template.clientName ? { clientName: template.clientName } : {}),
      },
    })
      .unwrap()
      .then((order) => {
        dispatch(setOrderFormData({
          ...(order as unknown as OrderEntity),
          deliveryMode: (order as { deliveryMode?: string }).deliveryMode || template.deliveryMode,
        } as OrderEntity));
        const dest = order.destinationLocation ?? template.destinationLocation;
        dispatch(
          setMapLocation({
            latitude: arcToMapCoordinate(dest?.latitude) || 0,
            longitude: arcToMapCoordinate(dest?.longitude) || 0,
            address: dest?.address ?? '',
          }),
        );
        navigate(`${routesConfig.orders}/create-mission/${order.id}`);
      })
      .catch((err: unknown) => {
        trackError(toTrackedError(err), { code: 'FE_0020', extra: { flow: 'materializeMissionTemplate' } });
        toast.error('Failed to create order from template');
      });
  };

  const onEdit = () => {
    if (!template) return;
    setEditDialogOpen(true);
  };

  const onEditSubmit = ({ name }: ISaveTemplateForm) => {
    if (!template) return;
    updateMissionTemplate({
      id: template.id,
      body: { name: name.trim() },
    })
      .unwrap()
      .then((updated) => {
        toast.success('Mission template updated');
        setEditDialogOpen(false);
        dispatch(setSelectedMissionTemplate(updated));
      })
      .catch((err: unknown) => {
        trackError(toTrackedError(err), { code: 'FE_0020', extra: { flow: 'updateMissionTemplate' } });
        toast.error('Failed to update mission template');
      });
  };

  const onDelete = async () => {
    if (!template) return;
    const ok = await confirm(
      `This will permanently remove template "${template.name}". This cannot be undone.`,
      'Delete mission template?',
    );
    if (!ok) return;

    deleteMissionTemplate(template.id)
      .unwrap()
      .then(() => {
        toast.success('Mission template deleted');
        dispatch(setSelectedMissionTemplate(null));
      })
      .catch((err: unknown) => {
        trackError(toTrackedError(err), { code: 'FE_0020', extra: { flow: 'deleteMissionTemplate' } });
        toast.error('Failed to delete mission template');
      });
  };

  if (!template) {
    return (
      <ArcBaseContent>
        <ArcStack py={3} px={5} height='100%' justifyContent='center' alignItems='center'>
          <ArcText.h5 color='text.secondary'>Select a mission template</ArcText.h5>
        </ArcStack>
      </ArcBaseContent>
    );
  }

  const deliveryModeLabel = formatDeliveryMode(template.deliveryMode) || EMPTY_STRING;

  const takeoff = template.takeoffLocation;
  const destination = template.destinationLocation;

  const routeWaypoints = (template.waypoints ?? []).filter((w) => w.type !== 'HOME');
  const takeOffWp = routeWaypoints.find((w) => !w.orderId) ?? routeWaypoints[0];
  const orderWp = routeWaypoints.find((w) => Boolean(w.orderId));

  return (
    <ArcBaseContent>
      <ArcStack py={3} px={5} paddingTop={2} width='100%' height='100%' spacing={2}>
        <ArcStack direction='row' alignItems='center' justifyContent='space-between' flexShrink={0}>
          <ArcText.h5 fontSize={20} fontWeight={500}>DETAILS</ArcText.h5>
          <ArcStack direction='row' spacing={1} alignItems='center'>
            <ArcTooltip title='Edit'>
              <ArcIconButton
                onClick={onEdit}
                disabled={isProceeding || isDeleting || isUpdating}>
                <EditNoteIcon />
              </ArcIconButton>
            </ArcTooltip>
            <ArcTooltip title='Delete'>
              <ArcIconButton
                isLoading={isDeleting}
                disabled={isProceeding || isUpdating}
                onClick={onDelete}>
                <DeleteOutlineIcon color='error' />
              </ArcIconButton>
            </ArcTooltip>
            <ArcStack>
              <ArcButton.brand
                isLoading={isProceeding}
                disabled={isUpdating || isDeleting}
                onClick={onProceed}
                endIcon={<ArrowForwardIcon sx={{ color: 'common.black' }} />}
                sx={headerButtonSx}>
              Proceed
              </ArcButton.brand>
            </ArcStack>
          </ArcStack>
        </ArcStack>

        <ArcStack direction='row' height='86%' spacing={1}>
          <ArcStack
            p='20px 35px'
            borderRadius={1}
            bgcolor='background.paper'
            width='50%'
            height='100%'
            spacing={3}
            sx={{ minHeight: 0, overflowY: 'auto' }}
          >
            <ArcStack spacing={3}>
              <ArcStack direction='row' spacing={3}>
                <ArcCellInfo label='Name' width='50%' labelProps={labelProps} textProps={textProps}>
                  {template.name}
                </ArcCellInfo>
                <ArcCellInfo label='HUB' width='50%' labelProps={labelProps} textProps={textProps}>
                  {template.hubName || EMPTY_STRING}
                </ArcCellInfo>
              </ArcStack>

              <ArcCellInfo label='Takeoff Location' width='100%' labelProps={labelProps} textProps={textProps}>
                {takeoff?.address || EMPTY_STRING}
              </ArcCellInfo>

              <ArcStack direction='row' spacing={3}>
                <ArcCellInfo label='Takeoff Latitude' width='50%' labelProps={labelProps} textProps={textProps}>
                  {arcFormatCoordinateDisplay(takeoff?.latitude)}
                </ArcCellInfo>
                <ArcCellInfo label='Takeoff Longitude' width='50%' labelProps={labelProps} textProps={textProps}>
                  {arcFormatCoordinateDisplay(takeoff?.longitude)}
                </ArcCellInfo>
              </ArcStack>

              <ArcCellInfo label='Destination Location' width='100%' labelProps={labelProps} textProps={textProps}>
                {destination?.address || EMPTY_STRING}
              </ArcCellInfo>

              <ArcStack direction='row' spacing={3}>
                <ArcCellInfo label='Destination Latitude' width='50%' labelProps={labelProps} textProps={textProps}>
                  {arcFormatCoordinateDisplay(destination?.latitude)}
                </ArcCellInfo>
                <ArcCellInfo label='Destination Longitude' width='50%' labelProps={labelProps} textProps={textProps}>
                  {arcFormatCoordinateDisplay(destination?.longitude)}
                </ArcCellInfo>
              </ArcStack>

              <ArcStack direction='row' spacing={3}>
                <ArcCellInfo label='Takeoff Altitude' width='50%' labelProps={labelProps} textProps={textProps}>
                  {arcSpatium(Number(takeOffWp?.altitude ?? takeoff?.altitude))}
                </ArcCellInfo>
                <ArcCellInfo label='Destination Altitude' width='50%' labelProps={labelProps} textProps={textProps}>
                  {arcSpatium(Number(orderWp?.altitude ?? destination?.altitude))}
                </ArcCellInfo>
              </ArcStack>

              <ArcStack direction='row' spacing={3}>
                <ArcCellInfo label='Delivery Altitude' width='50%' labelProps={labelProps} textProps={textProps}>
                  {arcSpatium(Number(template.deliveryAltitude))}
                </ArcCellInfo>
                <ArcCellInfo label='Cruise Speed' width='50%' labelProps={labelProps} textProps={textProps}>
                  {arcVelocitas(Number(template.speed))}
                </ArcCellInfo>
              </ArcStack>

              <ArcCellInfo label='Delivery Mode' width='100%' labelProps={labelProps} textProps={textProps}>
                {deliveryModeLabel}
              </ArcCellInfo>
            </ArcStack>
          </ArcStack>

          <ArcStack width='50%' height='100%' borderRadius={1} sx={{ overflow: 'hidden' }}>
            <ArcGoogleMap hideToolbar hideMapControls>
              <MissionTemplatesMap />
            </ArcGoogleMap>
          </ArcStack>
        </ArcStack>
      </ArcStack>

      <SaveTemplateDialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        defaultName={template.name}
        onSubmit={onEditSubmit}
        isSubmitting={isUpdating}
        title='Edit Template'
        submitLabel='Save'
      />
    </ArcBaseContent>
  );
};
